"use client";

import * as React from "react";
import type * as z from "zod";

import { cn } from "@/lib/utils";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { createUserSchema } from "@/lib/validations/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { env } from "@/env";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { FloatingLabelInput } from "@/components/ui/floating-label";

interface UserAuthFormProps extends React.HTMLAttributes<HTMLDivElement> {}

type FormData = z.infer<typeof createUserSchema>;

export function SignUpForm({ className, ...props }: UserAuthFormProps) {
  const { signIn } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(createUserSchema),
  });
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  async function onSubmit(data: FormData) {
    setIsLoading(true);

    try {
      const response = await fetch(`${env.NEXT_PUBLIC_API_URL}/auth/sign-up`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: data.email,
          username: data.username,
          password: data.password,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        if (responseData.errors) {
          const errorMessages = Object.values(responseData.errors)
            .map((error: any) => error.param.msg)
            .join(", ");
          throw new Error(errorMessages);
        }
        throw new Error(responseData.message || "Something went wrong");
      }

      // Sign in after successful registration
      await signIn({
        username: data.username,
        password: data.password,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={cn("grid gap-6", className)} {...props}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid gap-3">
          <div className="grid gap-2">
            <FloatingLabelInput
              id="username"
              label="Username"
              {...register("username")}
            />
            {errors?.username && (
              <p className="px-1 text-xs text-red-600">
                {errors.username.message}
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <FloatingLabelInput
              id="email"
              label="Email"
              type="email"
              {...register("email")}
            />
            {errors?.email && (
              <p className="px-1 text-xs text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <FloatingLabelInput
              id="password"
              label="Password"
              type="password"
              {...register("password")}
            />
            {errors?.password && (
              <p className="px-1 text-xs text-red-600">
                {errors.password.message}
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <FloatingLabelInput
              id="confirmPassword"
              label="Confirm Password"
              type="password"
              {...register("confirmPassword")}
            />
            {errors?.confirmPassword && (
              <p className="px-1 text-xs text-red-600">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>
          <Button disabled={isLoading}>
            {isLoading && <LoadingSpinner />}
            Sign up
          </Button>
        </div>
      </form>
    </div>
  );
}
